import { useState, useEffect } from 'react';
import { hotelApi, goToLoginPage, hotelLogout } from './hotelApi';
import HotelDashboard  from './pages/hotel/HotelDashboard';
import HotelTables     from './pages/hotel/HotelTables';
import HotelKitchen    from './pages/hotel/HotelKitchen';
import HotelBilling    from './pages/hotel/HotelBilling';
import HotelMenu       from './pages/hotel/HotelMenu';
import HotelRooms      from './pages/hotel/HotelRooms';
import HotelBookings   from './pages/hotel/HotelBookings';
import HotelInventory  from './pages/hotel/HotelInventory';
import HotelStaff      from './pages/hotel/HotelStaff';
import HotelSetup      from './pages/hotel/HotelSetup';

const NAV = [
  { key: 'dashboard',  icon: '🏠', label: 'Dashboard' },
  { key: 'tables',     icon: '🍽️', label: 'Tables & Orders' },
  { key: 'kitchen',    icon: '👨‍🍳', label: 'Kitchen' },
  { key: 'billing',    icon: '🧾', label: 'Billing' },
  { key: 'menu',       icon: '📋', label: 'Menu' },
  { key: 'rooms',      icon: '🛏️', label: 'Rooms' },
  { key: 'bookings',   icon: '📅', label: 'Bookings' },
  { key: 'inventory',  icon: '📦', label: 'Inventory' },
  { key: 'staff',      icon: '👥', label: 'Staff',  adminOnly: true },
  { key: 'setup',      icon: '⚙️', label: 'Setup',  adminOnly: true },
];

const TITLES = {
  dashboard:  '🏠 Dashboard',
  tables:     '🍽️ Tables & Orders',
  kitchen:    '👨‍🍳 Kitchen Display',
  billing:    '🧾 Billing',
  menu:       '📋 Menu Management',
  rooms:      '🛏️ Rooms',
  bookings:   '📅 Bookings',
  inventory:  '📦 Inventory',
  staff:      '👥 Staff',
  setup:      '⚙️ Hotel Setup',
};

function initials(n) {
  if (!n) return '?';
  return n.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

export default function HotelApp({ user }) {
  const [active, setActive]           = useState('dashboard');
  const [sidebarOpen, setSidebar]     = useState(true);
  const [isMobile, setIsMobile]       = useState(window.innerWidth < 900);
  const [drawerOpen, setDrawer]       = useState(false);
  const [hovered, setHovered]         = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [role, setRole]               = useState('admin');
  const [ownersid, setOwnersid]       = useState('');
  const [billingOrderId, setBillingOrderId] = useState(null);

  // Listen for session-expiry events fired by hotelApi
  useEffect(() => {
    const handler = () => setSessionExpired(true);
    window.addEventListener('hotel-session-expired', handler);
    return () => window.removeEventListener('hotel-session-expired', handler);
  }, []);

  // Ping session every 10 minutes
  useEffect(() => {
    const ping = async () => {
      try {
        const s = await hotelApi.getSession();
        if (!s || !s.authenticated) { setSessionExpired(true); return; }
        if (s.remainingSeconds <= 300) setSessionWarning(true);
        else setSessionWarning(false);
        if (s.role) setRole(s.role);
        if (s.ownersid) setOwnersid(s.ownersid);
      } catch (_) {}
    };
    ping();
    const id = setInterval(ping, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const navigate = (key) => {
    setActive(key);
    setHovered(false);
    if (isMobile) setDrawer(false);
    else setSidebar(false);
  };

  const displayName = user?.username || user?.name || 'Staff';

  const renderPage = () => {
    const props = { role };
    switch (active) {
      case 'dashboard': return <HotelDashboard onNavigate={navigate} role={role} />;
      case 'tables':    return <HotelTables {...props} ownersid={ownersid} onGoBilling={(orderId) => { setBillingOrderId(orderId); navigate('billing'); }} />;
      case 'kitchen':   return <HotelKitchen {...props} />;
      case 'billing':   return <HotelBilling {...props} initialOrderId={billingOrderId} onMount={() => setBillingOrderId(null)} />;
      case 'menu':      return <HotelMenu {...props} />;
      case 'rooms':     return <HotelRooms {...props} />;
      case 'bookings':  return <HotelBookings {...props} />;
      case 'inventory': return <HotelInventory {...props} />;
      case 'setup':     return role === 'admin' ? <HotelSetup /> :
        <div style={{ padding: 40, textAlign: 'center', color: '#c5221f' }}>Access denied</div>;
      case 'staff':     return role === 'admin' ? <HotelStaff /> :
        <div style={{ padding: 40, textAlign: 'center', color: '#c5221f' }}>Access denied</div>;
      default:          return null;
    }
  };

  const isExpanded = !isMobile && (sidebarOpen || hovered);
  const sidebarStyle = {
    width: isExpanded ? 220 : isMobile ? 220 : 60,
    minWidth: isExpanded ? 220 : isMobile ? 220 : 60,
    background: 'linear-gradient(180deg,#0d1f1a 0%,#0a1a14 100%)',
    height: '100vh', display: 'flex', flexDirection: 'column',
    transition: 'width .2s, min-width .2s',
    position: isMobile ? 'fixed' : 'relative',
    zIndex: isMobile ? 1200 : 1,
    left: isMobile && !drawerOpen ? -240 : 0,
    overflowY: 'auto', overflowX: 'hidden',
    boxShadow: (isMobile && drawerOpen) || isExpanded ? '4px 0 20px rgba(0,0,0,.35)' : 'none',
  };

  const showLabels = isExpanded || (isMobile && drawerOpen);

  if (sessionExpired) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg,#0d1f1a 0%,#0a1a14 100%)', fontFamily: 'Inter,system-ui,sans-serif' }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: '40px 48px', textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,.4)', maxWidth: 400 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#202124', marginBottom: 8 }}>Session Expired</div>
          <div style={{ fontSize: 14, color: '#5f6368', marginBottom: 24 }}>
            Your session has ended. Please log in again to continue.
          </div>
          <button onClick={goToLoginPage}
            style={{ background: '#137333', color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 28px', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
            Log In Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: 'Inter,system-ui,sans-serif', background: '#f4f6f9' }}>
      {/* Mobile overlay */}
      {isMobile && drawerOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1100 }}
          onClick={() => setDrawer(false)} />
      )}

      {/* Sidebar */}
      <aside style={sidebarStyle}
        onMouseEnter={() => { if (!isMobile) setHovered(true); }}
        onMouseLeave={() => { if (!isMobile) setHovered(false); }}
      >
        {/* Logo */}
        <div style={{ padding: '0 14px', height: 64, display: 'flex', alignItems: 'center', gap: 10,
          borderBottom: '1px solid rgba(255,255,255,.08)', background: '#071310', flexShrink: 0 }}>
          <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg,#34a853,#137333)',
            borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, fontSize: 20 }}>
            🏨
          </div>
          {showLabels && (
            <div style={{ overflow: 'hidden' }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap' }}>SmaBizZ</div>
              <div style={{ color: '#5a8a6a', fontSize: 11, marginTop: 1 }}>Hotel Mgmt</div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '8px 0' }}>
          {NAV.filter(item => !item.adminOnly || role === 'admin').map(item => (
            <div key={item.key}
              onClick={() => navigate(item.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: showLabels ? '10px 16px' : '10px 0',
                justifyContent: showLabels ? 'flex-start' : 'center',
                cursor: 'pointer', borderRadius: 8, margin: '1px 8px',
                background: active === item.key ? 'rgba(26,163,83,.25)' : 'transparent',
                borderLeft: active === item.key ? '3px solid #34a853' : '3px solid transparent',
                transition: 'background .15s',
              }}
            >
              <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
              {showLabels && (
                <span style={{ color: active === item.key ? '#e2e8f0' : '#7aaa8a', fontSize: 14,
                  fontWeight: active === item.key ? 700 : 500 }}>
                  {item.label}
                </span>
              )}
            </div>
          ))}
        </nav>

        {/* Sidebar footer */}
        <div style={{ padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,.08)',
          display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%',
            background: 'linear-gradient(135deg,#137333,#34a853)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
            {initials(displayName)}
          </div>
          {showLabels && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName}
              </div>
              <div style={{ fontSize: 11, color: role === 'admin' ? '#34a853' : '#9aa0a6' }}>
                {role === 'admin' ? '⭐ Admin' : `🏷️ ${role}`}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {/* Business title banner */}
        {user?.businesstitle && (
          <div style={{ width: '100%', background: 'linear-gradient(90deg,#0d3321,#1a4731)',
            textAlign: 'center', padding: '12px 24px', flexShrink: 0,
            boxShadow: '0 2px 8px rgba(0,0,0,.3)', zIndex: 20 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '.6px',
              textShadow: '0 1px 3px rgba(0,0,0,.2)' }}>
              {user.businesstitle}
            </span>
          </div>
        )}

        {/* Topbar */}
        <div style={{ height: 56, display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px',
          background: '#fff', borderBottom: '1px solid #e8eaed',
          boxShadow: '0 1px 4px rgba(0,0,0,.08)', flexShrink: 0, zIndex: 10 }}>
          <button
            onClick={() => isMobile ? setDrawer(!drawerOpen) : setSidebar(!sidebarOpen)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20,
              color: '#5f6368', padding: 4, flexShrink: 0 }}>
            ☰
          </button>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#202124', flex: 1 }}>
            {TITLES[active]}
          </div>
          <button onClick={hotelLogout}
            title="Sign out"
            style={{ background: 'none', border: '1px solid #e8eaed', borderRadius: 8,
              padding: '5px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              color: '#5f6368', display: 'flex', alignItems: 'center', gap: 5 }}>
            🚪 Sign Out
          </button>
        </div>

        {/* Session expiry warning */}
        {sessionWarning && (
          <div style={{ padding: '8px 16px', background: '#fef3c7', borderBottom: '1px solid #fbbf24',
            display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, flexShrink: 0 }}>
            <span>⚠️ Your session expires soon.</span>
            <button onClick={goToLoginPage}
              style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6,
                padding: '3px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              Refresh Session
            </button>
            <button onClick={() => setSessionWarning(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                color: '#92400e', fontWeight: 700, marginLeft: 'auto' }}>
              ×
            </button>
          </div>
        )}

        {/* Page content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {renderPage()}
        </div>

        {/* Footer */}
        <div style={{ height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#f4f6f9', borderTop: '1px solid #e8eaed',
          fontSize: 11, color: '#9aa0a6', flexShrink: 0 }}>
          Hotel Management · {new Date().getFullYear()} · SmaBizZ
        </div>
      </div>
    </div>
  );
}
